import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import '../../styles/home.css';

function HomePage() {
  const { currentUser } = useAuth();
  
  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/blue-landscape.jpg)` }}>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Social Aptitude</h1>
          <p className="hero-subtitle">From Awkward to Awesome: Practical Social Intelligence Training</p>
          <div className="hero-buttons">
            <Link to="/personal-questionnaire" className="primary-home-button">Begin Your EQ Journey</Link>
          </div>
        </div>
      </section>
      
      {/* EQ Introduction Section */}
      <section className="eq-intro-section">
        <div className="section-title">What is Emotional Intelligence?</div>
        <p className="section-subtitle">Emotional intelligence is the ability to recognize, understand, and manage our emotions and those of others—it's essential for social and professional success</p>
        
        <div className="eq-intro-content">
          <div className="eq-intro-item">
            <div className="eq-intro-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="42" height="42">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="eq-intro-title">The Definition</h3>
            <p className="eq-intro-description">Emotional intelligence (EQ) is the ability to identify, evaluate, control, and express emotions. People with high EQ can recognize their own emotions and those of others, use emotional information to guide thinking and behavior, and effectively manage emotions to adapt to environments or achieve goals.</p>
          </div>
          
          <div className="eq-intro-item">
            <div className="eq-intro-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="42" height="42">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="eq-intro-title">Why It Matters</h3>
            <p className="eq-intro-description">Research shows that emotional intelligence significantly impacts social relationships, career success, and mental health. People with high EQ tend to have more fulfilling relationships, are more effective at work, and better manage stress and challenges. Emotional intelligence may even be a stronger predictor of success than IQ.</p>
          </div>
          
          <div className="eq-intro-item">
            <div className="eq-intro-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="42" height="42">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="eq-intro-title">How to Improve</h3>
            <p className="eq-intro-description">Improving emotional intelligence is an ongoing process that includes developing self-awareness, practicing emotional regulation, cultivating empathy, and enhancing interpersonal skills. Through systematic learning and practice, anyone can improve their EQ level and achieve greater success in both personal and professional life.</p>
          </div>
        </div>
        
        <div className="eq-intro-cta">
          <button 
            className="scroll-down-button icon-only" 
            onClick={() => {
              const eqAssessmentSection = document.querySelector('.eq-assessment-section');
              if (eqAssessmentSection) {
                eqAssessmentSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <span className="scroll-arrow">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 13l5 5 5-5"></path>
                <path d="M7 7l5 5 5-5"></path>
              </svg>
            </span>
          </button>
        </div>
      </section>
      
      {/* EQ Assessment Process Section */}
      <section className="eq-assessment-section">
        <div className="section-title">
          <h2>Our EQ Assessment Process</h2>
          <p>Embark on a structured journey to develop your emotional intelligence</p>
        </div>
        
        <div className="process-flow">
          <div className="process-step">
            <div className="step-icon">📋</div>
            <h3>Questionnaire</h3>
            <p>Share your background, goals, and areas you'd like to improve</p>
          </div>
          
          <div className="flow-connector">→</div>
          
          <div className="process-step">
            <div className="step-icon">🧠</div>
            <h3>EQ Test</h3>
            <p>Evaluate your emotional intelligence through interactive scenarios</p>
          </div>
          
          <div className="flow-connector">→</div>
          
          <div className="process-step">
            <div className="step-icon">📊</div>
            <h3>Analysis Report</h3>
            <p>Receive a detailed breakdown of your emotional intelligence profile</p>
          </div>
          
          <div className="flow-connector">→</div>
          
          <div className="process-step">
            <div className="step-icon">🎯</div>
            <h3>Practice Scenarios</h3>
            <p>Apply your skills in realistic social situations tailored to your needs</p>
          </div>
        </div>
        
        <div className="cta-container">
          <Link to="/personal-questionnaire" className="cta-button">Start Your EQ Journey</Link>
        </div>
      </section>
      
      {/* Target Audience Section */}
      <section className="for-who-section">
        <div className="section-title">Who Benefits From Our Training?</div>
        <p className="section-subtitle">Whether you're climbing the corporate ladder or navigating social waters, we've got you covered.</p>
        
        <div className="for-who-content">
          <div className="for-who-image">
            <img src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="People in business meeting" />
          </div>
          
          <div className="for-who-text">
            <h2>Designed for professionals who want to:</h2>
            <ul className="for-who-list">
              <li className="for-who-item">
                <div className="for-who-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Master difficult workplace conversations and negotiate with confidence</span>
              </li>
              <li className="for-who-item">
                <div className="for-who-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Develop emotional intelligence to better understand colleagues and clients</span>
              </li>
              <li className="for-who-item">
                <div className="for-who-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Build authentic connections in professional and social settings</span>
              </li>
              <li className="for-who-item">
                <div className="for-who-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>Navigate cross-cultural interactions with sensitivity and insight</span>
              </li>
            </ul>
            <Link to="/sequential-test" className="primary-home-button">Start Your Assessment</Link>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="features-section">
        <div className="section-title">What We Offer</div>
        <p className="section-subtitle">Practical tools to help you navigate any social situation with confidence and authenticity</p>
        
        <div className="card-container">
          <div className="feature-card">
            <div className="card-image">
              <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Personalized EQ Testing" />
            </div>
            <div className="card-content">
              <h3 className="card-title">Personalized EQ Testing</h3>
              <p className="card-description">Take our customized emotional intelligence assessment designed to identify your unique strengths and development areas in social interactions.</p>
              <Link to="/sequential-test" className="card-button">Take the Test</Link>
            </div>
          </div>
          
          <div className="feature-card">
            <div className="card-image">
              <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Customized EQ Report Analysis" />
            </div>
            <div className="card-content">
              <h3 className="card-title">Customized EQ Report Analysis</h3>
              <p className="card-description">Receive a detailed analysis of your emotional intelligence profile with personalized recommendations for improvement and growth.</p>
              <Link to="/sequential-test" className="card-button">Get Your Report</Link>
            </div>
          </div>
          
          <div className="feature-card">
            <div className="card-image">
              <img src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Realistic Social Scenario Simulation" />
            </div>
            <div className="card-content">
              <h3 className="card-title">Realistic Social Scenario Simulation</h3>
              <p className="card-description">Practice with authentic social situations that mirror real-life challenges, complete with expert guidance on optimal responses and behaviors.</p>
              <Link to="/advanced-scenarios" className="card-button">Explore Scenarios</Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Premium Content Section */}
      <section className="premium-section">
        <div className="section-title">Premium Content</div>
        <p className="section-subtitle">Take your social skills to the next level with our premium scenarios</p>
        
        <div className="premium-content">
          <div className="premium-image">
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Premium content" />
          </div>
          
          <div className="premium-text">
            <h2>Unlock Advanced Training Materials</h2>
            <p>Our premium content is designed by experts in social psychology, leadership development, and workplace communication.</p>
            
            <div className="premium-benefits">
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="benefit-text">Access to 100+ exclusive advanced scenarios</span>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="benefit-text">Detailed expert analysis and recommendations</span>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="benefit-text">Personalized social intelligence development plan</span>
              </div>
            </div>
            
            <Link to={currentUser ? "/payment" : "/register"} className="primary-home-button">Get Premium Access</Link>
          </div>
        </div>
      </section>
      
      {/* Footer Section */}
      <section className="footer-section">
        <div className="footer-content">
          <div className="footer-links">
            <Link to="/" className="footer-link">Home</Link>
            <Link to="/advanced-scenarios" className="footer-link">Scenarios</Link>
            <Link to="/sequential-test" className="footer-link">Assessment</Link>
            {currentUser ? (
              <Link to="/payment" className="footer-link">Premium</Link>
            ) : (
              <Link to="/register" className="footer-link">Sign Up</Link>
            )}
            <a href="mailto:carl-1101@outlook.com" className="footer-link contact-link">
              Contact Us
              <span className="contact-tooltip">We're constantly improving our platform based on user feedback. Get in touch with any questions or suggestions.</span>
            </a>
          </div>
          
          <div className="copyright">
            &copy; {new Date().getFullYear()} Social Aptitude. All rights reserved.
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage; 